import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft } from "lucide-react";
import axios from "axios";
import SEO, { breadcrumbSchema } from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const res = await axios.get(`${API}/blog`);
        setPosts(res.data);
      } catch {
        console.error("Failed to fetch blog posts");
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, []);

  return (
    <main data-testid="blog-page" className="min-h-screen bg-[#FDFBF7] pt-28 pb-24">
      <SEO
        title="Journal"
        path="/blog"
        description="Insights and research from Dr. Erika Tajra on nutrigenomics, microbiome science, personalised medicine, and longevity strategies."
        type="blog"
        jsonLd={breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Journal", path: "/blog" },
        ])}
      />
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <Link
          to="/"
          data-testid="blog-back-link"
          className="inline-flex items-center gap-2 text-sm text-[#6B6B6B] hover:text-[#D4AF37] transition-colors mb-12"
        >
          <ArrowLeft size={14} /> Home
        </Link>

        <p className="text-sm font-medium tracking-widest uppercase text-[#D4AF37] mb-4">
          Journal
        </p>
        <h1 className="text-4xl md:text-5xl font-medium tracking-tight text-[#2D2D2D] mb-6">
          Insights & Research
        </h1>
        <p className="text-lg font-light leading-relaxed text-[#6B6B6B] mb-16 max-w-2xl">
          Exploring the latest in nutrigenomics, microbiome science, and personalized longevity.
        </p>

        {loading ? (
          <div className="space-y-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-40 bg-[#F5F2EB] animate-pulse" />
            ))}
          </div>
        ) : posts.length === 0 ? (
          <p className="text-[#6B6B6B]">No posts yet. Check back soon.</p>
        ) : (
          <div className="space-y-0">
            {posts.map((post, index) => (
              <Link
                key={post.id || post.slug}
                to={`/blog/${post.slug}`}
                data-testid={`blog-post-${index}`}
                className="group block border-t border-[rgba(45,45,45,0.08)] py-10 transition-colors"
              >
                <div className="flex items-start justify-between gap-8">
                  <div className="flex-1">
                    <p className="text-xs text-[#6B6B6B] mb-3">
                      {new Date(post.created_at).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </p>
                    <h2 className="text-2xl md:text-3xl font-normal text-[#2D2D2D] group-hover:text-[#D4AF37] transition-colors mb-3">
                      {post.title}
                    </h2>
                    <p className="text-base font-light text-[#6B6B6B] leading-relaxed line-clamp-2">
                      {post.excerpt}
                    </p>
                  </div>
                  <ArrowRight
                    size={20}
                    className="text-[#E6DCCA] group-hover:text-[#D4AF37] transition-colors mt-4 flex-shrink-0"
                  />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
